import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import { tradeSuggestionsApi, strategiesApi, companiesApi } from '@/services/api';
import { TradeSuggestionsItem, TradeSuggestionsResponse, SelectOption } from '@/types/client';
import { isGlobalCountryCode } from '@/utils/country';
import { formatNumber } from '@/utils/formatters';
import { DateRangeFilter } from '@/components/utils/filters';
import {
  PageFrame, PageHeader, FilterBar, TabBar, Button, Note,
  FilterMultiSelect, FilterAsyncMultiSelect, FilterOption,
} from '@/components/ui';
import shared from '@/components/utils/shared.module.css';
import css from './TradeSuggestions.module.css';
import TradeSuggestionsTable from './TradeSuggestionsTable';
import {
  filterItems,
  sortItems,
  totalRecommendationRows,
} from './tradeSuggestionColumns';

// Static category options — the full set is short enough to list.
const CATEGORY_OPTIONS: FilterOption[] = [
  'I', 'A', 'B', 'B-', 'C', 'W0', 'W1', 'W2', 'W3', 'E', 'H', 'R',
].map(value => ({ value, label: value }));

// Default selected categories
const DEFAULT_CATEGORY_VALUES = ['I', 'A', 'B', 'B-', 'C', 'W1', 'W2', 'W3'];
const DEFAULT_CATEGORIES = CATEGORY_OPTIONS.filter(cat => DEFAULT_CATEGORY_VALUES.includes(cat.value));
const DEFAULT_LOOKBACK_MONTHS = 4;

// Helper function to get date nmonths months ago
const getMonthsAgo = (nMonths: number) => {
  const date = new Date();
  date.setMonth(date.getMonth() - nMonths);
  return date.toISOString().split('T')[0];
};

// Strategy codes
const LONG_HORIZON_STRATEGY_CODES = ['LHF', 'LHF_AIF'];
const LONG_HORIZON_STRATEGY_LABEL = 'Long Horizon Domestic';
// SGS is surfaced in its own "Global Strategy" tab (non-India companies)
const SGS_STRATEGY_CODE = 'SGS';
const GLOBAL_STRATEGY_LABEL = 'Global';

interface TradeSuggestionsParams {
  categories_list?: string;
  strategy_codes?: string;
  isin_list?: string;
  start_date?: string;
  end_date?: string;
  suggestions_flag?: boolean;
}

export interface StrategyTabDef {
  key: string;
  label: string;
  data: TradeSuggestionsItem[];
  strategyKeys: string[];
  emptyMessage: string;
}

// Pre-bucketed, per-tab data + strategy keys derived from the loaded response.
export interface TabDataBuckets {
  otherStrategiesData: TradeSuggestionsItem[];
  otherStrategyKeys: string[];
  lhfData: TradeSuggestionsItem[];
  lhfStrategyKeys: string[];
  globalStrategyData: TradeSuggestionsItem[];
  globalStrategyKeys: string[];
}

// Build the strategy tabs the applied filter asks for and that actually have data.
// "All" (or an empty selection) shows every tab; otherwise a tab appears only when the
// applied strategies include one of its codes. A tab with no data is always dropped.
export const computeVisibleTabs = (
  appliedStrategies: SelectOption[],
  buckets: TabDataBuckets,
): StrategyTabDef[] => {
  const allSelected =
    appliedStrategies.length === 0 || appliedStrategies.some(s => s.value === 'all');
  const wantsLhf = allSelected || appliedStrategies.some(s => LONG_HORIZON_STRATEGY_CODES.includes(s.value as string));
  const wantsGlobal = allSelected || appliedStrategies.some(s => s.value === SGS_STRATEGY_CODE);
  const wantsCore =
    allSelected ||
    appliedStrategies.some(s => !LONG_HORIZON_STRATEGY_CODES.includes(s.value as string) && s.value !== SGS_STRATEGY_CODE);

  const tabs: StrategyTabDef[] = [];

  if (wantsCore && buckets.otherStrategiesData.length > 0) {
    tabs.push({
      key: 'core',
      label: 'Core Domestic',
      data: buckets.otherStrategiesData,
      strategyKeys: buckets.otherStrategyKeys,
      emptyMessage: 'No trade suggestions found for Core Domestic strategies.',
    });
  }
  if (wantsLhf && buckets.lhfData.length > 0) {
    tabs.push({
      key: 'lhf',
      label: LONG_HORIZON_STRATEGY_LABEL,
      data: buckets.lhfData,
      strategyKeys: buckets.lhfStrategyKeys,
      emptyMessage: 'No trade suggestions found for Long Horizon Domestic strategies.',
    });
  }
  if (wantsGlobal && buckets.globalStrategyData.length > 0) {
    tabs.push({
      key: 'global',
      label: GLOBAL_STRATEGY_LABEL,
      data: buckets.globalStrategyData,
      strategyKeys: buckets.globalStrategyKeys,
      emptyMessage: 'No trade suggestions found for Global strategies.',
    });
  }
  return tabs;
};

/** Companies are searched server-side — the master list runs to five figures, so
 *  it is never worth pulling in full just to populate a filter. */
const loadCompanyIsinOptions = async (inputValue: string): Promise<FilterOption[]> => {
  try {
    const response = await companiesApi.getCompanies({ search: inputValue, page_size: 500 });
    return response.results.map(company => ({
      value: company.isin,
      label: `${company.symbol} - ${company.name}`,
    }));
  } catch {
    return [];
  }
};

const TradeSuggestionsList: React.FC = () => {
  const [data, setData] = useState<TradeSuggestionsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [applyingFilters, setApplyingFilters] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter states with default values. An empty selection means "all".
  const [strategyOptions, setStrategyOptions] = useState<FilterOption[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<FilterOption[]>(DEFAULT_CATEGORIES);
  const [selectedStrategies, setSelectedStrategies] = useState<FilterOption[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<FilterOption[]>([]);
  const [loadingStrategies, setLoadingStrategies] = useState(true);
  const [startDate, setStartDate] = useState<string>(getMonthsAgo(DEFAULT_LOOKBACK_MONTHS));
  const [endDate, setEndDate] = useState<string>('');
  const [suggestionsOnly, setSuggestionsOnly] = useState<boolean>(true);

  // Tab state for Long Horizon separation
  const [activeTab, setActiveTab] = useState<number>(0);
  // Tracks strategies from the last applied/loaded response
  const [appliedStrategies, setAppliedStrategies] = useState<SelectOption[]>([]);

  // Table state — search and sort are client-side: the endpoint returns the
  // whole filtered set in one response. There is no pager: a company's rows
  // are merged with a real rowSpan, which cannot straddle a page boundary, so
  // the table scrolls instead — see TradeSuggestionsTable.
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sortField, setSortField] = useState<string>('symbol');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Holds the AbortController for the in-flight trade suggestions request so
  // rapid "Apply Filters" clicks or unmount can cancel the previous request.
  const tradeSuggestionsAbortRef = useRef<AbortController | null>(null);

  // 1. Load strategies on component mount
  useEffect(() => {
    const controller = new AbortController();

    const loadStrategies = async () => {
      try {
        setLoadingStrategies(true);
        const strategiesData = await strategiesApi.getStrategies({ page_size: 1000 }, controller.signal);
        setStrategyOptions(strategiesData.results.map(strat => ({ value: strat.code, label: strat.code })));
      } catch (err: any) {
        if (err.name === 'CanceledError' || err.name === 'AbortError') return;
        console.error('Error loading strategies:', err);
        if (err.code === 'ERR_NETWORK') {
          setError('Connection with Server Failed!');
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoadingStrategies(false);
        }
      }
    };

    loadStrategies();
    return () => controller.abort();
  }, []);

  // Load trade suggestions function
  const loadTradeSuggestions = useCallback(async () => {
    // Cancel any in-flight request before starting a new one
    tradeSuggestionsAbortRef.current?.abort();
    const controller = new AbortController();
    tradeSuggestionsAbortRef.current = controller;

    try {
      setError(null);

      // An empty selection means "all", so each list is only sent when narrowed.
      const params: TradeSuggestionsParams = {};

      if (selectedCategories.length > 0) {
        params.categories_list = selectedCategories.map(cat => cat.value).join(',');
      }

      if (selectedStrategies.length > 0) {
        params.strategy_codes = selectedStrategies.map(strat => strat.value).join(',');
      }

      if (selectedCompanies.length > 0) {
        params.isin_list = selectedCompanies.map(comp => comp.value).join(',');
      }

      if (startDate) {
        params.start_date = startDate;
      }

      if (endDate) {
        params.end_date = endDate;
      }

      if (suggestionsOnly) {
        params.suggestions_flag = true;
      }

      const response: TradeSuggestionsResponse = await tradeSuggestionsApi.getTradeSuggestions(params, controller.signal);
      setData(response.data);
      setAppliedStrategies([...selectedStrategies]);

      toast.success(`Trade suggestions loaded successfully! Found ${response.data.length} results.`);
    } catch (err: any) {
      if (err.name === 'CanceledError' || err.name === 'AbortError') return;
      console.error('Error loading trade suggestions:', err);
      const errorMessage = err.response?.data?.message || 'Failed to load trade suggestions';
      setError(errorMessage);
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
        setApplyingFilters(false);
      }
    }
  }, [selectedCategories, selectedStrategies, selectedCompanies, startDate, endDate, suggestionsOnly]);

  // 2. Load trade suggestions on mount, in parallel with the strategies — the initial
  // request doesn't use the strategy list (an empty selection means "all"), which only
  // feeds the filter dropdown.
  useEffect(() => {
    loadTradeSuggestions();
    return () => { tradeSuggestionsAbortRef.current?.abort(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Get all unique strategy codes for table headers, ordered by largest AUM first
  const allStrategyKeys = useMemo(() => {
    return [...new Set(data.flatMap(item => Object.keys(item.strategies)))]
      .sort((a, b) => {
        const aTotalAum = data.reduce((sum, item) => sum + (item.strategies[a]?.aum || 0), 0);
        const bTotalAum = data.reduce((sum, item) => sum + (item.strategies[b]?.aum || 0), 0);
        return bTotalAum - aTotalAum;
      });
  }, [data]);

  const lhfStrategyKeys = useMemo(
    () => allStrategyKeys.filter(k => LONG_HORIZON_STRATEGY_CODES.includes(k)),
    [allStrategyKeys]
  );

  const globalStrategyKeys = useMemo(
    () => allStrategyKeys.filter(k => k === SGS_STRATEGY_CODE),
    [allStrategyKeys]
  );

  const otherStrategyKeys = useMemo(
    () => allStrategyKeys.filter(k => !LONG_HORIZON_STRATEGY_CODES.includes(k) && k !== SGS_STRATEGY_CODE),
    [allStrategyKeys]
  );

  // Split data into LHF and Core Strategies lists with filtered recommendations.
  // Both are Domestic-only: the backend seeds a suggestion entry for every strategy
  // code on every company regardless of country, so without the country gate global
  // companies would appear here as well as in the Global tab.
  const lhfData = useMemo(() => {
    return data
      .filter(item => !isGlobalCountryCode(item.country_code))
      .map(item => {
        const filteredRecs = item.recommendations.filter(rec => {
          const lhfSuggestions = LONG_HORIZON_STRATEGY_CODES
            .map(code => rec.suggestions?.[code])
            .filter(s => s != null);
          if (lhfSuggestions.length === 0) return false;
          if (suggestionsOnly) {
            return lhfSuggestions.some(s => s.trade_type || s.trade_description);
          }
          return true;
        });
        if (filteredRecs.length === 0) return null;
        return { ...item, recommendations: filteredRecs };
      })
      .filter((item): item is TradeSuggestionsItem => item !== null);
  }, [data, suggestionsOnly]);

  const otherStrategiesData = useMemo(() => {
    return data
      .filter(item => !isGlobalCountryCode(item.country_code))
      .map(item => {
        const filteredRecs = item.recommendations.filter(rec =>
          otherStrategyKeys.some(strategyCode => {
            const suggestion = rec.suggestions?.[strategyCode];
            if (!suggestion) return false;
            if (suggestionsOnly) {
              return suggestion.trade_type || suggestion.trade_description;
            }
            return true;
          })
        );
        if (filteredRecs.length === 0) return null;
        return { ...item, recommendations: filteredRecs };
      })
      .filter((item): item is TradeSuggestionsItem => item !== null);
  }, [data, otherStrategyKeys, suggestionsOnly]);

  // Global Strategy: non-India companies with SGS suggestions
  const globalStrategyData = useMemo(() => {
    return data
      .filter(item => isGlobalCountryCode(item.country_code))
      .map(item => {
        const filteredRecs = item.recommendations.filter(rec => {
          const suggestion = rec.suggestions?.[SGS_STRATEGY_CODE];
          if (!suggestion) return false;
          if (suggestionsOnly) {
            return suggestion.trade_type || suggestion.trade_description;
          }
          return true;
        });
        if (filteredRecs.length === 0) return null;
        return { ...item, recommendations: filteredRecs };
      })
      .filter((item): item is TradeSuggestionsItem => item !== null);
  }, [data, suggestionsOnly]);

  // Build the strategy tabs the applied filter asks for and that actually have data.
  const visibleTabs = useMemo(
    () =>
      computeVisibleTabs(appliedStrategies, {
        otherStrategiesData,
        otherStrategyKeys,
        lhfData,
        lhfStrategyKeys,
        globalStrategyData,
        globalStrategyKeys,
      }),
    [
      appliedStrategies,
      otherStrategiesData,
      otherStrategyKeys,
      lhfData,
      lhfStrategyKeys,
      globalStrategyData,
      globalStrategyKeys,
    ]
  );

  // Keep the active tab in range when the set of visible tabs changes
  useEffect(() => {
    if (activeTab >= visibleTabs.length && activeTab !== 0) {
      setActiveTab(0);
    }
  }, [visibleTabs.length, activeTab]);

  // The tab actually on screen. With no tabs (nothing matched) the table still
  // renders its own empty state, over the full un-bucketed strategy list.
  const currentTab = visibleTabs[activeTab];
  const tabData = currentTab ? currentTab.data : [];
  const tabStrategyKeys = currentTab ? currentTab.strategyKeys : allStrategyKeys;

  const visibleItems = useMemo(
    () => sortItems(filterItems(tabData, searchTerm), sortField, sortDir),
    [tabData, searchTerm, sortField, sortDir],
  );

  const handleSort = useCallback((field: string) => {
    setSortDir(prev => (sortField === field ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'));
    setSortField(field);
  }, [sortField]);

  const applyFilters = () => {
    setApplyingFilters(true);
    loadTradeSuggestions();
  };

  const clearFilters = () => {
    setSelectedCategories([]);
    setSelectedStrategies([]);
    setSelectedCompanies([]);
    setStartDate('');
    setEndDate('');
    setSuggestionsOnly(false);
    toast.info('Filters cleared');
  };

  // Every narrowed control counts as one applied filter, which is what the
  // Filters button badges.
  const activeFilterCount = [
    selectedCategories.length > 0,
    selectedStrategies.length > 0,
    selectedCompanies.length > 0,
    !!startDate,
    !!endDate,
    suggestionsOnly,
  ].filter(Boolean).length;

  const filterSlot = (
    <>
      <FilterMultiSelect
        label="Categories"
        options={CATEGORY_OPTIONS}
        value={selectedCategories}
        onChange={setSelectedCategories}
      />

      <FilterMultiSelect
        label="Strategies"
        options={strategyOptions}
        value={selectedStrategies}
        onChange={setSelectedStrategies}
        loading={loadingStrategies}
      />

      <FilterAsyncMultiSelect
        label="Companies"
        value={selectedCompanies}
        onChange={setSelectedCompanies}
        loadOptions={loadCompanyIsinOptions}
      />

      <DateRangeFilter
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        startLabel="Start Date"
        endLabel="End Date"
        startId="ts-start-date"
        endId="ts-end-date"
      />

      <label className={css['check-filter']}>
        <input
          type="checkbox"
          checked={suggestionsOnly}
          onChange={e => setSuggestionsOnly(e.target.checked)}
        />
        Triggered only
      </label>

      <div className={shared['filter-action']}>
        <Button variant="primary" onClick={applyFilters} disabled={applyingFilters}>
          {applyingFilters ? 'Applying…' : 'Apply Filters'}
        </Button>
      </div>
    </>
  );

  return (
    <PageFrame
      header={
        <PageHeader
          title="Trade Suggestions"
          subtitle="Analyst and IC recommendations against what each strategy already holds, with the trade each one implies."
        />
      }
    >
      <FilterBar
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search by symbol, company, category or recommendation…"
        filters={filterSlot}
        activeFilterCount={activeFilterCount}
        onClearFilters={clearFilters}
      />

      {visibleTabs.length > 1 && (
        <TabBar
          ariaLabel="Strategy group"
          value={currentTab?.key ?? visibleTabs[0].key}
          onChange={key => setActiveTab(visibleTabs.findIndex(tab => tab.key === key))}
          tabs={visibleTabs.map(tab => ({ value: tab.key, label: tab.label }))}
        />
      )}

      {error && <Note tone="error">{error}</Note>}

      <div className={`${shared['table-panel']} ${shared['panel-grow']}`}>
        <TradeSuggestionsTable
          items={visibleItems}
          columnItems={tabData}
          strategyKeys={tabStrategyKeys}
          sortField={sortField}
          sortDir={sortDir}
          onSort={handleSort}
          loading={loading}
          emptyMessage={currentTab?.emptyMessage ?? 'No trade suggestions found with the current filters.'}
        />
        {!loading && (
          <div className={shared['table-foot']}>
            <span className={shared['count-info']}>
              {visibleItems.length > 0 ? (
                <>
                  Showing <strong>{formatNumber(visibleItems.length, 0)}</strong> companies ·{' '}
                  <strong>{formatNumber(totalRecommendationRows(visibleItems), 0)}</strong> recommendations
                </>
              ) : (
                'No records'
              )}
            </span>
          </div>
        )}
      </div>
    </PageFrame>
  );
};

export default TradeSuggestionsList;
